<template lang="pug">
div
  v-card.round-xl(outlined)
    //- amount details
    div.py-4
      template(v-if="isFetchingInvoice || isFetchingTransactions")
        v-skeleton-loader.px-4(type="list-item-three-line@2")
      template(v-else)
        div.px-3
          //- price by price tiers
          div.mb-2.mx-1(v-if="totalBalance > 0 && totalByPriceTiers.length > 1")
            template(v-for="(ptier, index) in totalByPriceTiers")
              div.d-flex(:key="'ttlptier-' + index")
                div.text-xs.neutral500--text {{ ptier.name }}
                v-spacer
                div.text-xs.neutral500--text.no-wrap.ml-2 {{ ptier.price | displayPrice }}
          //- subtotal
          div.d-flex.align-center.mx-1
            div.text-sm.font-bold.neutral700--text Subtotal
            v-spacer
            div.text-sm.font-bold.neutral700--text {{ invoice.subtotal_amount | displayPrice($currency) }}
          //- adjustment
          div.mt-2.mx-1(v-if="allowedToAdjust")
            div(v-if="invoice.invoice_discount !== 0")
              .d-flex.align-center
                .text-sm.neutral700--text Adjustment
                v-btn.ml-2(x-small icon @click="openConfirmDeleteAdjustmentDialog")
                  v-icon(small) mdi-close
                v-spacer
                .text-sm.neutral700--text {{ invoice.invoice_discount > 0 ? '-' : '' }} {{ Math.abs(invoice.invoice_discount) | displayPrice($currency) }}
            template(v-else)
              div.d-flex.align-center
                payment-adjustment(:total-amount="invoice.total_amount" :invoice="invoice" @updated="fetchInvoice")
                v-spacer
                .text-sm.neutral700--text {{ 0 | displayPrice($currency) }}
          //- tax
          div.mt-2.mx-1
            div.d-flex.align-center
              payment-tax(:rates="taxRates" :tax-rate="invoice.extra_data && invoice.extra_data.tax_rate" hide-price :tax-amount="invoice.tax_amount" :invoice="invoice" @updated="fetchInvoice")
              v-spacer
              .text-sm.neutral700--text.no-wrap {{ invoice.tax_amount | displayPrice($currency) }}
          //- offers
          template(v-if="Auth('root', 'subscribe_marketing')")
            template(v-for="(offer, idx) in invoice.redeems")
              div.mt-2(:key="`of-${idx}`")
                applied-offer(:offerItem="offer" @updated="fetchInvoice")
          //- total
          div.d-flex.align-center.mt-2.mx-1
            div.text-sm.font-bold.neutral700--text Total
            v-spacer
            div.text-sm.font-bold.neutral700--text {{ invoice.total_amount | displayPrice($currency) }}
          v-divider.my-4
          //- amount due
          div.d-flex.align-center.mx-1
            div.text-md.font-semibold.neutral700--text Amount due
            v-spacer
            div.text-md.font-semibold(:class="totalBalance ? 'danger500--text' : 'neutral700--text'") {{ totalBalance | displayPrice($currency) }}
        //- payment list
        v-card.round-xl.mt-4.mx-2(outlined)
          v-card-title.neutral200.pa-2
            div.text-sm.font-medium.neutral700--text Payments
            v-spacer
            y-button(v-if="transactions.length" label="Manage" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" height="24" unset-min-width size="xxs" button-class="px-2" @click="showManagePaymentDialog = true")
            y-button.ml-2(label="Add" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" height="24" unset-min-width size="xxs" button-class="px-2" @click="showRecordPaymentDialog = true")
          v-divider
          v-card-text.pa-0
            template(v-if="transactions.length")
              template(v-for="(transaction, idx) in transactions")
                v-divider(v-if="idx")
                payment-item(:transaction="transaction" :key="`trx-${idx}`" @updated="updateTransaction" @deleted="updateTransaction")
            template(v-else)
              template(v-if="defaultPanel")
                pre-selected-payment-item(:panel="defaultPanel" @updated="updateTransaction" @deleted="defaultPanel = null" )
              template(v-else)
                div.text-xs.neutral500--text.text-center.my-3 No payment record
        //- e-invoice
        div.px-4
          e-invoice-section.mt-4(:invoice="invoice" :transactions="transactions")
        //- complete visitation
        div.mt-4.px-4(v-if="!registration")
          y-button.mt-4(label="Visitation completed" size="sm" block disabled)
        div.mt-4.px-4(v-else-if="!isHq")
          template(v-if="registration.status === 'completed'")
            y-button.mt-4( label="Visitation completed" size="sm" block disabled)
          template(v-else)
            template(v-if="showCompletePaymentLater && registration.status === 'checkout'")
              y-checkbox.mb-n2(v-model="completePaymentLater")
                template(#label)
                  .text-sm.neutral700--text Complete payment later
            y-button.mt-4(label="Complete visitation" size="sm" block :disabled="isCompleteVisitDisabled" :loading="isLoading" @click="completeVisitation")
          
  .dialogs
    manage-payment-dialog(v-if="showManagePaymentDialog" :open.sync="showManagePaymentDialog" @updated="updateTransaction")
    record-payment-dialog(v-if="showRecordPaymentDialog" :open.sync="showRecordPaymentDialog" @updated="updateTransaction")
    delete-payment-adjustment-dialog(v-if="showConfirmDeleteAdjustmentDialog" :open.sync="showConfirmDeleteAdjustmentDialog" @deleted="fetchInvoice")

</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import PaymentAdjustment from '@/components/clinic/checkout/PaymentAdjustment.vue'
import PaymentTax from '@/components/clinic/checkout/PaymentTax.vue'
import PaymentItem from '@/components/clinic/checkout/PaymentItem.vue'
import PreSelectedPaymentItem from '@/components/clinic/checkout/PreSelectedPaymentItem.vue'
import AppliedOffer from '@/components/clinic/checkout/marketing/AppliedOffer.vue'
import ManagePaymentDialog from '@/components/clinic/checkout/ManagePaymentDialog.vue'
import RecordPaymentDialog from '@/components/clinic/checkout/RecordPaymentDialog.vue'
import DeletePaymentAdjustmentDialog from '@/components/clinic/dialogs/DeletePaymentAdjustmentDialog.vue'
import EInvoiceSection from '@/components/clinic/checkout/EInvoiceSection.vue'

export default {
  components: { PaymentAdjustment, PaymentTax, PaymentItem, PreSelectedPaymentItem, AppliedOffer, ManagePaymentDialog, RecordPaymentDialog, DeletePaymentAdjustmentDialog, EInvoiceSection },
  data () {
    return {
      isLoading: false,
      isFetchingInvoice: false,
      isFetchingTransactions: false,
      showManagePaymentDialog: false,
      showRecordPaymentDialog: false,
      showConfirmDeleteAdjustmentDialog: false,
      completePaymentLater: false,
      defaultPanel: null,
      taxRates: [],
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      invoice: 'treatment/getInvoice',
      registration: 'treatment/getRegistration',
      transactions: 'treatment/getTransactions',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq',
      isDoctor: 'user/isDoctor'
    }),
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
    allowedToAdjust () {
      if (this.company.genes.find(gene => gene.feature === 'lock_payment_adjustment') && !this.isAdminOrDoctor) return false
      return true
    },
    totalPaid () {
      if (!this.invoice.pid) {
        return 0
      }
      if (this.transactions.length === 0) {
        return 0
      }
      return this.transactions.reduce((sum, trans) => {
        sum += trans.amount
        return sum
      }, 0)
    },
    totalBalance () {
      if (!this.invoice.pid) {
        return 0
      }
      return this.invoice.total_amount - this.totalPaid
    },
    amountToClaim () {
      if (!this.invoice.pid) {
        return 0
      }
      if (this.transactions.length === 0) {
        return 0
      }
      return this.transactions.reduce((sum, trans) => {
        if (trans.channel === 'panel' && !trans.paid) {
          sum += trans.amount
        }
        return sum
      }, 0)
    },
    totalByPriceTiers () {
      if (!this.invoice.pid) {
        return []
      }
      let pricetiers = []
      this.invoice.products.forEach(product => {
        let tierName = product.extra_data?.price_tier_name
        if (tierName) {
          let price = product.price * product.quantity
          let pricetier = pricetiers.find(f => f.name === tierName)
          if (pricetier) {
            pricetier.price += price
          } else {
            pricetiers.push({ name: tierName, price: price })
          }
        }
      })
      return pricetiers
    },
    isCompleteVisitDisabled () {
      let disabled = false
      if (this.totalBalance !== 0 && !this.completePaymentLater) {
        disabled = true
      }
      if (this.registration && this.registration.status !== 'checkout') {
        disabled = true
      }   
      return disabled
    },
    showCompletePaymentLater () {
      return this.totalBalance !== 0
    },
  },
  mounted () {
    this.checkDefaultPanel()
    this.fetchTransactions()
    this.fetchTaxRates()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchInvoice () {
      this.isFetchingInvoice = true
      this.$axios.get(`/invoices/${this.leadPid}`)
        .then(resp => {
          this.$store.commit('treatment/setInvoice', resp.data)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingInvoice = false
        })
    },
    fetchTransactions() {
      this.isFetchingTransactions = true
      let params = {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        pid: this.leadPid,
        page_size: -1
      }
      const endpoint = this.isHq ? '/hq/transactions' : '/transactions'
      this.$axios.get(endpoint, { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.$store.commit('treatment/setTransactions', resp.data.results)
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingTransactions = false
        })
    },
    fetchTaxRates () {
      this.$axios.get('/tax_rates?', { params: { company: this.isHq ? this.hqSelectedCompany.id : this.company.id } })
        .then(({ data }) => {
          if (data && data.results) {
            this.taxRates = data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    checkDefaultPanel () {
      if (this.registration?.default_payment?.panel) {
        this.defaultPanel = this.registration.default_payment.panel
        this.defaultPanel.staffId = this.registration.default_payment.staff_id
      }
    },
    updateTransaction () {
      this.fetchInvoice()
      this.fetchTransactions()
    },
    openConfirmDeleteAdjustmentDialog () {
      this.showConfirmDeleteAdjustmentDialog = true
    },
    completeVisitation () {
      this.isLoading = true
      this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, { status: 'completed' })
        .then(() => {
          this.createActivityLog('complete_visit', 'Visit completed')
          this.$emit('completed')
        })
        .catch((err) => {
          let message = this.$t('error_message.unable_to_complete_request')   
          if (err.response.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: 'checkout',
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>

</style>